import { buildApps } from '@/seo/catalog';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  Car,
  Check,
  ClipboardList,
  Code2,
  CreditCard,
  FileText,
  FlaskConical,
  Globe,
  HeartHandshake,
  Layers,
  LayoutGrid,
  MessageCircle,
  Minus,
  MonitorPlay,
  PackageSearch,
  Plus,
  Rocket,
  ShoppingBag,
  Sparkles,
  Stethoscope,
  Ticket,
  Wallet,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { SiteMeta } from '@/components/site/SiteMeta';
import { usePlansCatalog } from '@/context/PlansContext';
import {
  effectivePlanPriceCents,
  formatPlanPrice,
  type PublicPlan,
} from '@/content/plans';
import { appTiers, fetchPublicApps, formatAppPrice, usageUnitFor, type PublicApp } from '@/content/apps';
import { tierPriceLabel } from '@/content/addon-tiers';
import { TierComparison } from '@/components/site/TierComparison';
import { legalRoutes } from '@/content/company';
import {
  addonUnlockHint,
  addonPriceCents,
  buyablePlans,
  calculatorCheckoutHref,
  computeCalculatorTotalCents,
  extraChannelUnitCents,
  extraSeatUnitCents,
  formatAddonCalculatorPrice,
  isApproximateAddonPrice,
  planDescription,
  purchasableAddons,
  resolveAddonTiers,
  suggestPlan,
} from '@/lib/calculator';

const iconByName: Record<string, LucideIcon> = {
  Stethoscope,
  Wrench,
  Car,
  FileText,
  CreditCard,
  Wallet,
  CalendarDays,
  Sparkles,
  Code2,
  MessageCircle,
  PackageSearch,
  ShoppingBag,
  ClipboardList,
  HeartHandshake,
  Globe,
  MonitorPlay,
  Ticket,
  FlaskConical,
  Boxes,
  LayoutGrid,
};

function resolveIcon(name: string | null | undefined, fallback: LucideIcon = Boxes): LucideIcon {
  if (name && iconByName[name]) return iconByName[name];
  return fallback;
}

/** Ícone distinto por código de plano (start / pro / business / scale / enterprise). */
const PLAN_ICONS: Record<string, LucideIcon> = {
  start: Zap,
  pro: Rocket,
  business: Briefcase,
  scale: Layers,
  enterprise: Building2,
};

function planIcon(plan: PublicPlan): LucideIcon {
  return PLAN_ICONS[plan.code.toLowerCase()] ?? LayoutGrid;
}

function Stepper({
  label,
  value,
  onChange,
  min = 1,
  max = 200,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="calc-stepper">
      <span className="calc-stepper-label">{label}</span>
      <div className="calc-stepper-ctrl">
        <button
          type="button"
          aria-label={`Diminuir ${label}`}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
        >
          <Minus strokeWidth={2.4} width={16} height={16} />
        </button>
        <span className="calc-stepper-val" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          aria-label={`Aumentar ${label}`}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
        >
          <Plus strokeWidth={2.4} width={16} height={16} />
        </button>
      </div>
    </div>
  );
}

function CalculatorLineItem({
  icon: Icon,
  title,
  description,
  price,
  checked,
  onToggle,
  exclusive,
  disabled,
  meta,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  price: string;
  checked: boolean;
  onToggle: () => void;
  exclusive?: boolean;
  disabled?: boolean;
  meta?: string | null;
}) {
  return (
    <label className={`calc-row${checked ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
      <span className="calc-avatar" aria-hidden>
        <Icon strokeWidth={2} />
      </span>
      <span className="calc-row-text">
        <span className="calc-row-title">{title}</span>
        <span className="calc-row-desc">{description}</span>
        {meta ? <span className="calc-row-meta">{meta}</span> : null}
      </span>
      <span className="calc-row-price">{price}</span>
      <span className="calc-check">
        <input
          type={exclusive ? 'radio' : 'checkbox'}
          name={exclusive ? 'calc-plan' : undefined}
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
        />
        <span className="calc-check-ui" aria-hidden>
          {checked ? <Check strokeWidth={3} width={14} height={14} /> : null}
        </span>
      </span>
    </label>
  );
}

/**
 * Escolha de nível de um add-on (ex.: Essencial ou Completo). Aparece quando o add-on
 * está no carrinho. A diferença entre os níveis fica aberta, porque é o que a pessoa
 * precisa ver para decidir.
 */
function AddonTierPicker({
  app,
  tierCode,
  onChange,
}: {
  app: PublicApp;
  tierCode: string;
  onChange: (code: string) => void;
}) {
  const tiers = appTiers(app);
  return (
    <div className="calc-tiers" role="group" aria-label={`Nível de ${app.name}`}>
      <div className="calc-tier-cards">
        {tiers.map((tier) => {
          const picked = tier.code === tierCode;
          return (
            <label key={tier.code} className={`calc-tier${picked ? ' is-on' : ''}`}>
              <input
                type="radio"
                name={`tier-${app.slug}`}
                checked={picked}
                onChange={() => onChange(tier.code)}
              />
              <span className="calc-tier-head">
                <strong>{tier.name}</strong>
                <span className="calc-tier-price">{tierPriceLabel(tier)}</span>
              </span>
              {tier.description ? <span className="calc-tier-desc">{tier.description}</span> : null}
              {tier.included_usage ? (
                <span className="calc-tier-meta">
                  {tier.included_usage.toLocaleString('pt-BR')} {usageUnitFor(app.pricing?.usage_unit ?? 'uso', tier.included_usage)}/mês
                  {tier.overage_unit_price_cents
                    ? `, depois ${formatAppPrice(tier.overage_unit_price_cents)} cada`
                    : ''}
                </span>
              ) : null}
            </label>
          );
        })}
      </div>
      <details className="calc-tier-diff" open>
        <summary>O que muda entre os níveis</summary>
        <TierComparison app={app} highlight={tierCode} />
      </details>
    </div>
  );
}

function CartLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="calc-cart-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function CalculadoraPage() {
  // Planos e apps nascem com o catálogo do build: a calculadora já aparece
  // pronta no HTML gerado, e a busca ao vivo só atualiza por cima.
  const { plans, loading: plansLoading, reload } = usePlansCatalog();
  const [apps, setApps] = useState<PublicApp[]>(buildApps);

  const [seatsNeeded, setSeatsNeeded] = useState(3);
  const [channelsNeeded, setChannelsNeeded] = useState(1);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [pickedTiers, setPickedTiers] = useState<Record<string, string>>({});
  const [extraSeats, setExtraSeats] = useState(0);
  const [extraChannels, setExtraChannels] = useState(0);
  const [suggestionApplied, setSuggestionApplied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPublicApps()
      .then((data) => {
        if (!cancelled && data.length > 0) setApps(data);
      })
      .catch(() => {
        /* segue com o catálogo do build */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const catalog = useMemo(() => buyablePlans(plans), [plans]);
  const addons = useMemo(() => purchasableAddons(apps), [apps]);
  const suggestion = useMemo(
    () => suggestPlan(plans, seatsNeeded, channelsNeeded),
    [plans, seatsNeeded, channelsNeeded],
  );

  // Auto-seleciona plano sugerido quando necessidade muda (e na 1ª carga).
  useEffect(() => {
    if (!suggestion) return;
    setSelectedPlanCode(suggestion.plan.code);
    setExtraSeats(suggestion.overflowSeats);
    setExtraChannels(suggestion.overflowChannels);
    setSuggestionApplied(true);
  }, [suggestion?.plan.code, suggestion?.overflowSeats, suggestion?.overflowChannels, seatsNeeded, channelsNeeded]);

  const selectedPlan: PublicPlan | null = useMemo(() => {
    if (!selectedPlanCode) return suggestion?.plan ?? null;
    return catalog.find((p) => p.code === selectedPlanCode) ?? suggestion?.plan ?? null;
  }, [catalog, selectedPlanCode, suggestion]);

  // Add-on com níveis sempre sai com um nível: o escolhido, ou o mais barato.
  const addonTiers = useMemo(
    () => resolveAddonTiers(apps, selectedAddons, pickedTiers),
    [apps, selectedAddons, pickedTiers],
  );

  const totalCents = computeCalculatorTotalCents({
    plan: selectedPlan,
    addonSlugs: selectedAddons,
    apps,
    extraSeats,
    extraChannels,
    addonTiers,
  });

  const hasApprox = selectedAddons.some((slug) => {
    const app = apps.find((a) => a.slug === slug);
    return app ? isApproximateAddonPrice(app, addonTiers[slug]) : false;
  });

  const checkoutHref = selectedPlan
    ? calculatorCheckoutHref({
        planCode: selectedPlan.code,
        addonSlugs: selectedAddons,
        extraSeats,
        extraChannels,
        addonTiers,
      })
    : '/assinar';

  function toggleAddon(slug: string) {
    setSelectedAddons((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  let body: ReactNode;
  if (plansLoading) {
    body = <p className="note" style={{ fontStyle: 'normal' }}>Carregando catálogo…</p>;
  } else if (catalog.length === 0) {
    body = (
      <div style={{ textAlign: 'center' }}>
        <p className="note" style={{ fontStyle: 'normal' }}>
          Não foi possível carregar os planos agora.
        </p>
        <button type="button" className="btn btn-ghost" style={{ marginTop: 12 }} onClick={reload}>
          Tentar de novo
        </button>
      </div>
    );
  } else {
    body = (
      <div className="calc-layout">
        <div className="calc-main">
          <section className="calc-section">
            <h2>1. Quantas pessoas e canais você precisa?</h2>
            <p className="calc-section-lead">
              A gente sugere o plano que cabe. Depois você ainda pode comprar extras sem mudar o plano.
            </p>
            <div className="calc-need">
              <Stepper label="Assentos (usuários)" value={seatsNeeded} onChange={setSeatsNeeded} />
              <Stepper label="Canais (WhatsApp)" value={channelsNeeded} onChange={setChannelsNeeded} />
            </div>
            {suggestionApplied && suggestion ? (
              <p className="calc-suggest-hint">
                Sugerido: <strong>{suggestion.plan.name}</strong>
                {suggestion.overflowSeats + suggestion.overflowChannels > 0
                  ? ` · +${suggestion.overflowSeats} assento(s) e +${suggestion.overflowChannels} canal(is) extras`
                  : null}
                {suggestion.needsEnterprise ? (
                  <>
                    {' '}
                    · para operações maiores,{' '}
                    <Link to={legalRoutes.contact}>fale com vendas</Link>
                  </>
                ) : null}
              </p>
            ) : null}
          </section>

          <section className="calc-section">
            <h2>2. Plano</h2>
            <p className="calc-section-lead">Um plano por conta. O sugerido já vem marcado.</p>
            <div className="calc-list" role="radiogroup" aria-label="Planos">
              {catalog.map((plan) => (
                <CalculatorLineItem
                  key={plan.id}
                  exclusive
                  icon={planIcon(plan)}
                  title={plan.name}
                  description={planDescription(plan)}
                  price={`${formatPlanPrice(effectivePlanPriceCents(plan))}/mês`}
                  checked={selectedPlan?.code === plan.code}
                  onToggle={() => {
                    setSelectedPlanCode(plan.code);
                    // Trocar de plano não zera extras manuais — só o overflow da sugestão.
                  }}
                />
              ))}
            </div>
          </section>

          <section className="calc-section">
            <h2>3. Add-ons</h2>
            <p className="calc-section-lead">
              Módulos opcionais. Itens com <strong>~</strong> têm consumo variável além da mensalidade.
            </p>
            {addons.length === 0 ? (
              <p className="note" style={{ fontStyle: 'normal' }}>
                Nenhum add-on disponível no momento.
              </p>
            ) : (
              <div className="calc-list">
                {addons.map((app) => {
                  const tiers = appTiers(app);
                  const on = selectedAddons.includes(app.slug);
                  return (
                    <div key={app.slug} className="calc-group">
                      <CalculatorLineItem
                        icon={resolveIcon(app.icon)}
                        title={app.name}
                        description={app.description ?? 'Add-on Operax'}
                        price={
                          tiers.length > 0 && !on
                            ? `a partir de ${formatPlanPrice(addonPriceCents(app))}/mês`
                            : `${formatAddonCalculatorPrice(app, addonTiers[app.slug])}/mês`
                        }
                        meta={
                          tiers.length > 0
                            ? `${tiers.length} níveis: ${tiers.map((t) => t.name).join(' ou ')}`
                            : addonUnlockHint(app)
                        }
                        checked={on}
                        onToggle={() => toggleAddon(app.slug)}
                      />
                      {on && tiers.length > 0 ? (
                        <AddonTierPicker
                          app={app}
                          tierCode={addonTiers[app.slug]}
                          onChange={(code) => setPickedTiers((prev) => ({ ...prev, [app.slug]: code }))}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="calc-section">
            <h2>4. Extras (além do plano)</h2>
            <p className="calc-section-lead">
              Mais assentos ou canais sem trocar de plano.
              {selectedPlan && extraSeatUnitCents(selectedPlan) > 0
                ? ` Assento extra: ${formatPlanPrice(extraSeatUnitCents(selectedPlan))}/mês.`
                : null}
              {selectedPlan && extraChannelUnitCents(selectedPlan) > 0
                ? ` Canal extra: ${formatPlanPrice(extraChannelUnitCents(selectedPlan))}/mês.`
                : null}
            </p>
            <div className="calc-need">
              <Stepper
                label="Assentos extras"
                value={extraSeats}
                min={0}
                max={100}
                onChange={setExtraSeats}
              />
              <Stepper
                label="Canais extras"
                value={extraChannels}
                min={0}
                max={50}
                onChange={setExtraChannels}
              />
            </div>
          </section>
        </div>

        <aside className="calc-cart" aria-label="Resumo">
          <div className="calc-cart-card">
            <h3>Seu resumo</h3>
            {selectedPlan ? (
              <CartLine
                label={`Plano ${selectedPlan.name}`}
                value={formatPlanPrice(effectivePlanPriceCents(selectedPlan))}
              />
            ) : null}
            {selectedAddons.map((slug) => {
              const app = apps.find((a) => a.slug === slug);
              if (!app) return null;
              return (
                <CartLine
                  key={slug}
                  label={addonTiers[slug] ? `${app.name}, ${appTiers(app).find((t) => t.code === addonTiers[slug])?.name}` : app.name}
                  value={`${formatAddonCalculatorPrice(app, addonTiers[slug])}`}
                />
              );
            })}
            {extraSeats > 0 && selectedPlan ? (
              <CartLine
                label={`${extraSeats} assento(s) extra(s)`}
                value={formatPlanPrice(extraSeats * extraSeatUnitCents(selectedPlan))}
              />
            ) : null}
            {extraChannels > 0 && selectedPlan ? (
              <CartLine
                label={`${extraChannels} canal(is) extra(s)`}
                value={formatPlanPrice(extraChannels * extraChannelUnitCents(selectedPlan))}
              />
            ) : null}
            <div className="calc-cart-total">
              <span>Total mensal{hasApprox ? ' estimado' : ''}</span>
              <strong>
                {hasApprox ? '~ ' : ''}
                {formatPlanPrice(totalCents)}
              </strong>
            </div>
            <p className="calc-cart-note">
              Mensagens WhatsApp/Meta ficam na conta da Meta — fora deste total.
            </p>
            <a className="btn btn-primary calc-cart-cta" href={checkoutHref}>
              Assinar com esta configuração →
            </a>
            <Link className="calc-cart-alt" to="/precos">
              Prefiro ver os planos
            </Link>
          </div>
        </aside>
      </div>
    );
  }

  return (
    <>
      <SiteMeta
        title="Calculadora de preço - Operax"
        description="Monte seu plano Operax: assentos, canais e add-ons. Veja o total mensal antes de assinar."
        path="/calculadora"
      />

      <section className="page-hero">
        <div className="hero-grid-bg" />
        <div className="shell page-hero-in rev">
          <h1>Calcule seu preço</h1>
          <p className="lead">
            Informe quantos usuários e canais precisa, marque os add-ons e veja o total. Se quiser, fecha direto —
            ou volta aos planos e assina no fluxo normal.
          </p>
        </div>
      </section>

      <section className="pricing calc-page">
        <div className="shell">{body}</div>
      </section>
    </>
  );
}
